import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { DrawerAction } from './types';

/** Width variants for the DrawerPanel. */
const WIDTH_MAP: Record<DrawerPanelWidth, number> = {
  narrow: 400,
  medium: 560,
  wide: 720,
};

type DrawerPanelWidth = 'narrow' | 'medium' | 'wide';

export interface DrawerPanelProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called when the drawer requests to close (close button or backdrop click). */
  onClose: () => void;
  /** Title displayed in the fixed header. */
  title: string;
  /** Width variant of the panel. Defaults to 'medium'. */
  width?: DrawerPanelWidth;
  /** Content rendered in the scrollable area. */
  children: React.ReactNode;
  /** Action buttons rendered in the sticky footer. */
  actions?: DrawerAction[];
  /** Shows a loading spinner overlay when true. */
  loading?: boolean;
  /** Optional stable test id applied to the drawer paper (e.g. "site-form"). */
  testId?: string;
}

/**
 * DrawerPanel — A slide-out panel from the right edge used for create/edit forms.
 *
 * Features:
 * - Slides in from the right with a backdrop overlay
 * - Fixed header with title and close button
 * - Scrollable content area
 * - Sticky footer with configurable action buttons
 * - Blocks background interaction while open
 * - Three width variants: narrow (400px), medium (560px), wide (720px)
 *
 * @see Requirements 11.1, 11.2, 11.3
 */
export const DrawerPanel: React.FC<DrawerPanelProps> = ({
  open,
  onClose,
  title,
  width = 'medium',
  children,
  actions,
  loading = false,
  testId,
}) => {
  const panelWidth = WIDTH_MAP[width];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Use a modal backdrop to block background interaction
      ModalProps={{
        keepMounted: false,
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
      PaperProps={{
        'data-testid': testId,
        sx: {
          width: panelWidth,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" component="h2" noWrap>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close drawer"
          edge="end"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 2,
          position: 'relative',
        }}
      >
        {loading && (
          <Backdrop
            open
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            <CircularProgress />
          </Backdrop>
        )}
        {children}
      </Box>

      {/* Sticky Footer with Action Buttons */}
      {actions && actions.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 3,
              py: 2,
              flexShrink: 0,
            }}
          >
            {actions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant}
                color={action.color ?? 'primary'}
                onClick={action.onClick}
                disabled={action.disabled}
                data-testid={action.testId}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default DrawerPanel;
